/* BrainGain / Enterprise. No cart, inventory, price or app configuration mutations. */
(() => {
  'use strict';
  if (customElements.get('bg-preorder-notice')) return;
  const DAY = 86400000;

  function dateValue(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const number = Date.parse(`${value}T00:00:00Z`);
    return Number.isFinite(number) && new Date(number).toISOString().slice(0, 10) === value ? number : null;
  }

  class BGPreorderNotice extends HTMLElement {
    connectedCallback() {
      if (!this.isConnected) return;
      // Deferred script normally upgrades parsed elements. Also support editor inserts.
      if (!this.querySelector('[data-bg-variants]')) {
        this.readyTimer = setTimeout(() => this.connectedCallback(), 0);
        return;
      }
      if (this.connected) return;
      this.connected = true;
      this.section = this.closest('.js-product');
      if (!this.section) return;
      this.headline = this.querySelector('[data-bg-headline]');
      this.dateLine = this.querySelector('[data-bg-date]');
      this.selectedId = this.dataset.selectedVariant || null;
      try {
        this.variants = JSON.parse(this.querySelector('[data-bg-variants]').textContent);
        this.dayFormatter = new Intl.DateTimeFormat('en-US', {
          timeZone: this.dataset.timezone, year: 'numeric', month: '2-digit', day: '2-digit'
        });
        const locale = this.dataset.locale || 'en';
        this.displayFormatter = new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : locale, {
          timeZone: 'UTC', day: 'numeric', month: 'long'
        });
      } catch (error) {
        // Do not silently calculate expiry in the visitor's timezone if config is invalid.
        console.error('[BrainGain pre-order] Check dispatch timezone and notice data.', error);
        this.section.dataset.bgPreorderState = 'pending';
        return;
      }
      const picker = this.section.querySelector('variant-picker');
      if (picker && typeof picker.getSelectedVariant === 'function') {
        this.selectedId = picker.getSelectedVariant()?.id || null;
      }
      this.onVariant = (event) => {
        if (event.target.closest('.js-product') !== this.section) return;
        this.selectedId = event.detail?.variant?.id || null;
        this.refresh();
      };
      this.onReturn = () => { if (!document.hidden) this.refresh(); };
      this.onPageShow = () => {
        const currentPicker = this.section.querySelector('variant-picker');
        if (currentPicker && typeof currentPicker.getSelectedVariant === 'function') {
          this.selectedId = currentPicker.getSelectedVariant()?.id || null;
        }
        this.refresh();
      };
      this.section.addEventListener('on:variant:change', this.onVariant);
      document.addEventListener('visibilitychange', this.onReturn);
      window.addEventListener('pageshow', this.onPageShow);
      this.refresh();
    }

    disconnectedCallback() {
      clearTimeout(this.readyTimer);
      clearTimeout(this.midnightTimer);
      this.section?.removeEventListener('on:variant:change', this.onVariant);
      document.removeEventListener('visibilitychange', this.onReturn);
      window.removeEventListener('pageshow', this.onPageShow);
      this.connected = false;
    }

    storeDay(now) {
      const parts = {};
      this.dayFormatter.formatToParts(new Date(now)).forEach(({ type, value }) => { parts[type] = value; });
      return dateValue(`${parts.year}-${parts.month}-${parts.day}`);
    }

    scheduleMidnight(now, today) {
      clearTimeout(this.midnightTimer);
      // Find the next store-local date boundary. Handles 23/25-hour DST days.
      let low = now;
      let high = now + 27 * 3600000;
      while (high - low > 500) {
        const middle = Math.floor((low + high) / 2);
        if (this.storeDay(middle) === today) low = middle;
        else high = middle;
      }
      this.midnightTimer = setTimeout(() => this.refresh(), high - now + 100);
    }

    refresh() {
      if (!this.isConnected) return;
      const now = Date.now();
      const today = this.storeDay(now);
      this.scheduleMidnight(now, today);
      const variantId = this.selectedId == null ? null : String(this.selectedId);
      // Null means an impossible/unselected combination; suppress both promises.
      if (!variantId) {
        this.hidden = true;
        this.section.dataset.bgPreorderState = 'unavailable';
        return;
      }
      const rawDate = this.dataset.defaultVariant === 'true'
        ? this.dataset.productDate
        : this.variants[variantId];
      const target = dateValue(rawDate);
      const days = target === null ? -1 : Math.round((target - today) / DAY);
      if (days < 0) {
        this.hidden = true;
        this.section.dataset.bgPreorderState = 'normal';
        return;
      }
      const text = days === 0 ? this.dataset.messageToday
        : days === 1 ? this.dataset.messageOne
          : this.dataset.messageDays.replace('[days]', String(days));
      const formattedDate = this.displayFormatter.format(new Date(target));
      const subtext = this.dataset.messageDate.replace('[date]', formattedDate);
      if (this.headline.textContent !== text) this.headline.textContent = text;
      if (this.dateLine.textContent !== subtext) this.dateLine.textContent = subtext;
      this.section.dataset.bgPreorderState = 'active';
      this.hidden = false;
    }
  }
  customElements.define('bg-preorder-notice', BGPreorderNotice);
})();
